import type Combatant from "@/encounter/model/Combatant.ts";
import type {CombatTurnToken, TurnState} from "@/api/TurnApi.ts";
import {combatantsSort} from "@/encounter/combatants/CombatantHooks.ts";
import {isAllowedToAct} from "./TurnHooks.ts";

export function turnOrder(state: TurnState, combatants: Combatant[]): string[] {
    return state.round > 0 ? state.turnOrder : roundCombatants(state, combatants, 1).map(actor => actor.id);
}

export function navigateTurn(state: TurnState, combatants: Combatant[], direction: 1 | -1):
    {round: number; activeCombatantId: string; turnOrder: string[]} | undefined {
    combatants = combatParticipants(state, combatants);
    const eligible = combatants.filter(actor => canActInRound(state, actor, state.round));
    if (state.round <= 0) return;
    const allowed = new Set(eligible.map(actor => actor.id));
    const order = turnOrder(state, combatants);
    const index = order.indexOf(state.activeCombatantId ?? "");
    if (index < 0) {
        const candidates = direction === 1 ? order : [...order].reverse();
        const activeCombatantId = candidates.find(id => allowed.has(id));
        if (activeCombatantId) return {round: state.round, activeCombatantId, turnOrder: order};
        return direction === 1 ? firstTurnInRound(state, combatants, state.round + 1) : undefined;
    }
    for (let offset = 1; offset <= order.length; offset++) {
        const nextIndex = index + direction * offset;
        if (nextIndex < 0) {
            return;
        }
        if (nextIndex >= order.length) {
            return firstTurnInRound(state, combatants, state.round + 1);
        }
        if (allowed.has(order[nextIndex])) {
            return {round: state.round, activeCombatantId: order[nextIndex], turnOrder: order};
        }
    }
    return direction === 1 ? firstTurnInRound(state, combatants, state.round + 1) : undefined;
}

export function combatParticipants(state: TurnState, combatants: Combatant[]): Combatant[] {
    const ids = new Set(state.participants.map(participant => participant.combatantId));
    return combatants.filter(actor => ids.has(actor.id));
}

export function canActInRound(state: TurnState, actor: Combatant, round: number): boolean {
    return (actor.currentLife > 0 || !actor.hostile) && state.participants.find(entry => entry.combatantId === actor.id)?.initiative != null
        && participatesInRound(state, actor.id, round);
}

export function participatesInRound(state: TurnState, id: string, round: number): boolean {
    const participant = state.participants.find(entry => entry.combatantId === id);
    return !!participant && round >= Math.max(1, participant.joinedRound, participant.firstEligibleRound)
        && !(round === 1 && participant.surprised);
}

export function combatTokens(state: TurnState, combatants: Combatant[]): CombatTurnToken[] {
    const entries = new Map(state.participants.map(entry => [entry.combatantId, entry]));
    return combatants.map(actor => ({...actor, initiative: entries.get(actor.id)?.initiative ?? undefined}));
}

export function roundCombatants(state: TurnState, combatants: Combatant[], round: number): CombatTurnToken[] {
    return combatTokens(state, combatants).filter(actor => canActInRound(state, actor, round))
        .sort((a, b) => a.initiative !== b.initiative ? (b.initiative ?? -Infinity) - (a.initiative ?? -Infinity) : combatantsSort(a, b));
}

/** Jump directly to the next possible entry round; never loop over empty/dead encounters. */
export function firstTurnInRound(state: TurnState, combatants: Combatant[], round: number) {
    const living = combatTokens(state, combatParticipants(state, combatants)).filter(isAllowedToAct);
    if (!living.length) return;
    const firstRound = Math.min(...living.map(actor => {
        const entry = state.participants.find(participant => participant.combatantId === actor.id);
        return Math.max(round, entry?.joinedRound ?? 1, entry?.firstEligibleRound ?? 1, entry?.surprised ? 2 : 1);
    }));
    const order = roundCombatants(state, combatants, firstRound).map(actor => actor.id);
    return order.length ? {round: firstRound, activeCombatantId: order[0], turnOrder: order} : undefined;
}
